import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  ChevronRight,
  Clock,
  Loader2,
  Sparkles,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ROUTE } from '@/config';
import { cn } from '@/lib/utils';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import ServiceIcon from '@/components/OrderForm/ServiceIcon';
import { useFetchServicePreferences } from '@/hooksApi';
import useServicePriceQuotes from '@/hooksApi/useServicePriceQuotes';
import useResolveService from '@/hooksApi/useResolveService';
import UnavailablePreferenceNotice from '@/components/OrderForm/UnavailablePreferenceNotice';
import useNewOrderStore from '@/stores/useNewOrderStore';
import useUserStore, { selectUsesServices } from '@/stores/userStore';
import {
  buildServiceMetrics,
  metricLabel,
  SERVICE_METRIC_ORDER,
  type ServiceMetric,
} from '@/utils/serviceMetrics';
import type { ServiceCatalogueItem } from '@/types';

/**
 * SVC-STEP — "Select Service", the wizard step between Order Details and
 * Summary.
 *
 * Until now a Boxy Select merchant went straight from the form to the summary,
 * and the service was a dropdown tucked into the form's right column. That
 * asked them to pay a difference between two names with nothing to weigh it
 * against. This step puts the evidence next to the price: how long it takes,
 * how often it arrives, how long a return takes to come back.
 *
 * ══ THE ONE SUBTLE THING ══
 * NO SERVICE IS PRE-SELECTED. The row that IS selected on arrival means "use
 * my saved rules", and it sends no `service_code` at all.
 *
 * It is tempting to pre-select the recommended service so the screen opens
 * looking decided. That would be wrong, and quietly: the recommendation is
 * `effective_default_service_code`, the ACCOUNT default, resolved with NO
 * destination — while the merchant's standing rules may say "Karbala ships
 * Economy". Committing it as an explicit `service_code` would override that
 * province rule on every order and the merchant would never see it happen, the
 * same trap ServiceLevelCard documents for the old dropdown.
 *
 * So the wire behaviour is unchanged from a blank screen: no service chosen,
 * no `service_code`, backend resolves exactly as today, province rules
 * included. What the explicit row adds is REACHABILITY (QA SVC-F3). The first
 * cut left "no choice" as the absence of a click, which meant that after one
 * stray click the merchant could never get back to it — the feature's central
 * answer was unreachable, where the dropdown it replaced had `allowClear`.
 * Naming the state also stops it being a secret: "use my saved rules" is the
 * behaviour, written down, instead of something inferred from an empty form.
 *
 * Resolving the province rule here to pre-select the truly-effective service
 * was the alternative; it would put a second copy of ServicePreferenceResolver
 * in the client, to drift against the real one. Not worth it for a pre-tick.
 *
 * ⚠️ The control is the in-house RadioGroupItem, NOT a native <input>. QA
 * UI-F1: `.sd-scope input` carries `appearance:none; outline:none; background:
 * transparent`, so a native radio inside this scope renders as NOTHING — no
 * dot, no ring, no checked state. The arrival state and every selection were
 * invisible, which is total for a screen whose entire job is showing what is
 * selected. `outline:none` took the keyboard indicator with it (UI-F3).
 */

// RadioGroup cannot hold `null`, and NOTHING IS PRE-SELECTED — so the group
// still needs a value meaning "no row is chosen yet". This sentinel is that
// and nothing more: it exists only in the radio's value space and is
// translated back to null before anything reads it.
//
// 🔴 IT NO LONGER HAS A ROW. The "use my saved rules" row was removed on the
// owner's decision, 2026-08-25 — see the note on the RadioGroup below.
const NO_SELECTION = '__no_selection__';

/**
 * One glyph per stat, so the three read as a scannable set rather than three
 * lines of prose. Keyed off the metric's stable `id` — never its label, which
 * is translated.
 */
const METRIC_ICON = {
  delivery: Clock,
  success_rate: CheckCircle2,
  resolution: Zap,
} as const;

// buildServiceMetrics owns which stats exist; this map owns their glyphs, and
// the two are edited in different files. A metric with no entry here renders
// `<undefined />`, which throws and takes the whole step down — so the lookup
// falls back rather than trusting the maps to stay in step.
const metricIcon = (id: ServiceMetric['id']) => METRIC_ICON[id] ?? Clock;

// ⚠️⚠️ THE TABLE TRACKS EXIST ONLY FROM `xl` UP, AND `lg` IS NOT A TYPO.
//
// 🔴 I SHIPPED THIS AT `lg` AND UI-F2 CAME STRAIGHT BACK. BOXY::UI-QA measured
// it at 1024 in Arabic, on the real page:
//
//     tracks = [0, 88,88,88,88,88, 48, 116]
//               ↑ الاسم at ZERO px      ↑ price crushed to 48, min is 96
//
// The five metric tracks hold their 88px floor and take the space from the two
// tracks that can yield. It is arithmetic, not a rendering quirk:
//
//     5×88 + 96 + 116 + (7 gaps × 16) = 764   minimum the row needs
//     row at vw=1024                  = 716
//
// So anything under ~1072 viewport is starved, and `lg` IS 1024 — the gate was
// placed exactly at the width it was supposed to protect. 1024 is iPad
// landscape and every small laptop. `xl` (1280) clears 764 with room; at
// vw=1440 the row measured 1132 and the tracks were healthy.
//
// ⭐ A BREAKPOINT IS A CLAIM ABOUT AVAILABLE WIDTH, AND IT HAS TO BE CHECKED
// AGAINST THE SUM OF THE MINIMUMS. I picked `lg` because a table "feels like"
// a desktop thing, never adding the tracks up.
//
// The original note this replaces, still true and still the reason the floor
// is 88px: five tracks in a 513px container gave ~89px each and truncated
// every Arabic label, leaving "الحل في نف…" and "في الوقت ا…" indistinguishable.
// An unlabelled number is not evidence, it is noise. Below `xl` the row falls
// back to the stacked layout, where each metric carries its own label inline.
//
// NAME | <N metrics> | PRICE | ACTION. One entry per metric count because a
// template literal produces a class Tailwind never generates.
const TABLE_COLUMNS: Record<number, string> = {
  0: 'xl:grid-cols-[minmax(0,1fr)_minmax(96px,auto)_116px]',
  1: 'xl:grid-cols-[minmax(0,1fr)_repeat(1,minmax(88px,1fr))_minmax(96px,auto)_116px]',
  2: 'xl:grid-cols-[minmax(0,1fr)_repeat(2,minmax(88px,1fr))_minmax(96px,auto)_116px]',
  3: 'xl:grid-cols-[minmax(0,1fr)_repeat(3,minmax(88px,1fr))_minmax(96px,auto)_116px]',
};

// ⭐ THE ARITHMETIC ABOVE IS NOW COMFORTABLE, AND THE GATE STAYS AT `xl`
// ANYWAY. With the set cut to three, the minimum is 3×88 + 96 + 116 + 5×16 =
// 556 against a 716px row at vw=1024, so `lg` would fit — the constraint that
// forced `xl` has gone. Moving the gate is still a layout change nobody asked
// for and nobody has measured in Arabic, and the sub-`xl` fallback is the
// layout UI-QA signed off. Left where it is, on purpose, with the number
// written down so the next reader does not re-derive it.

// Mirrors the settings page's RADIO_ITEM so the two service surfaces cannot
// drift. `mt-0.5` aligns the control with the first line of a multi-line label
// rather than the vertical centre of the whole block.
const RADIO_ITEM =
  'mt-0.5 shrink-0 border-sd-input text-sd-foreground focus-visible:ring-sd-ring ring-offset-sd-background';
/**
 * The redesign's Select / Selected control.
 *
 * ⚠️ NOT a <button>. The row is a <label> wrapping a real RadioGroupItem, and
 * the whole row is the hit target — so this is the VISUAL of a button over a
 * radio that still exists. Swapping to real buttons would have looked identical
 * and thrown away the radio group's keyboard semantics (arrow keys, roving
 * focus, one-of-N announced to a screen reader) for a control that is, in fact,
 * one-of-N.
 *
 * `aria-hidden` because the radio underneath already announces the state; two
 * announcements for one control is worse than none.
 */
const SelectPill = ({ active, t }: { active: boolean; t: TFunction }) => (
  <span
    aria-hidden='true'
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md px-4 text-xs font-semibold transition-colors',
      active
        ? 'border border-sd-primary bg-transparent text-sd-foreground'
        : 'bg-sd-muted text-sd-foreground/80',
    )}
  >
    {String(active ? t('selected') : t('select'))}
  </span>
);

const SelectService = () => {
  const { t, i18n } = useTranslation();
  const usesServices = useUserStore(selectUsesServices);
  const isRTL = i18n.dir() === 'rtl';
  const direction = isRTL ? 'rtl' : 'ltr';
  const navigate = useNavigate();
  // BOXY-588 — why the merchant was sent here: the service on the order cannot
  // reach this address (the backend's sentence, or the edit form's own check).
  const serviceRefusal = (useLocation().state as { serviceRefusal?: string } | null)
    ?.serviceRefusal;

  const { orderPayload, setOrderPayload, isEditMode, editOrderUid } = useNewOrderStore();
  // Same destination the navigation resolved on, so the rail drawn on this
  // page cannot contradict the decision that sent the merchant here.
  const traceProvinceCode = useNewOrderStore((state) => state.contactDisplayInfo?.provinceCode);

  // Read for `unavailable_preference` ONLY. The step decision itself is not
  // re-derived here: the merchant is already on this page, and a second source
  // for one skip decision is exactly the SVC-F2 defect (a step the flow
  // skipped, rendered anyway). Same query key as the navigation hook, so this
  // is the cache, not a second request.
  // BOXY-113 — asked with the cold flag and the destination REGION, the same
  // key the add-order page decided the step on.
  const { data: resolution } = useResolveService(
    traceProvinceCode,
    true,
    Boolean(orderPayload?.temperature_control),
    orderPayload?.contact?.region_uid,
    {
      saved_pick_up_address_uid: orderPayload?.saved_pick_up_address_uid,
      pick_up_address_uid: orderPayload?.pick_up_address_uid,
    },
    orderPayload?.boxes,
  );

  const { data, isLoading } = useFetchServicePreferences();
  // BOXY-530 (owner 2026-10-11) — a service no carrier can use for THIS order
  // stays LISTED, greyed out with its reason, the cold-chain pattern: no
  // carrier delivers to the destination (`not_served`), none collects from the
  // pickup province (`pickup_not_served`, Basra), or the address is remote.
  // Hiding it left the merchant guessing; leaving it live failed at Ship Now.
  // An older backend without `services` falls back to `available_service_codes`;
  // with neither, nothing is blocked (fail open).
  const coveredCodes = resolution?.available_service_codes;
  const services = useMemo<ServiceCatalogueItem[]>(() => data?.services ?? [], [data]);
  const blockedReasons = useMemo(() => {
    const reasons = new Map<string, string>();
    (resolution?.services ?? []).forEach((s) => {
      if (!s.available) reasons.set(s.code, s.unavailable_reason ?? 'not_served');
    });
    if (!resolution?.services && Array.isArray(coveredCodes)) {
      services.forEach((s) => {
        if (!coveredCodes.includes(s.code)) reasons.set(s.code, 'not_served');
      });
    }
    return reasons;
  }, [resolution, coveredCodes, services]);

  // The metric tracks shared by every row. A metric earns a column when ANY
  // service measures it; a service that does not measure it gets an EMPTY cell
  // in that column, never a 0 or a dash.
  //
  // An empty cell is not the thing the null rule forbids. That rule bans
  // rendering a VALUE the service does not have, because "0%" and "—" both
  // read as results. A blank track claims nothing at all — it is the absence
  // of a stat, in the position where that stat would be, which is exactly what
  // is true.
  const metricColumns = useMemo(
    () =>
      SERVICE_METRIC_ORDER.filter((id) =>
        services.some((service) => buildServiceMetrics(t, service).some((m) => m.id === id)),
      ),
    [services, t],
  );

  // Restored from the payload so stepping back from the summary shows the
  // merchant what they picked, rather than silently forgetting it.
  const [chosen, setChosen] = useState<string | null>(orderPayload?.service_code ?? null);

  const selectService = (value: string) => setChosen(value === NO_SELECTION ? null : value);

  // BOXY-559 OBS-2 (owner 2026-10-11) — the ONE exception to "nothing is
  // pre-selected": Save cannot reach this remote address and the backend fell
  // back to Select. Committing Select is not overriding a rule here — a manual
  // create never switches on its own, so leaving it unpicked only gets refused.
  const remoteFallback =
    resolution?.source === 'remote_area_fallback' &&
    resolution.service_code &&
    !blockedReasons.has(resolution.service_code)
      ? resolution.service_code
      : null;

  // §6.3.2 — a preselected service that cannot reach this address moves to the
  // backend's fallback (Boxy Select), so the merchant sees ITS price before
  // confirming instead of a choice the create would refuse. With nothing
  // chosen yet, the remote-area fallback is preselected; a valid pick stands.
  useEffect(() => {
    if (chosen && blockedReasons.has(chosen)) {
      const fallback = resolution?.service_code ?? null;
      setChosen(fallback && !blockedReasons.has(fallback) ? fallback : null);
    } else if (!chosen && remoteFallback) {
      setChosen(remoteFallback);
    }
  }, [chosen, blockedReasons, resolution, remoteFallback]);

  // Priced only where it can ship: a blocked row shows no price, and asking
  // for one would only fetch the refusal.
  const usableServices = useMemo(
    () => services.filter((s) => !blockedReasons.has(s.code)),
    [services, blockedReasons],
  );
  const quotes = useServicePriceQuotes(orderPayload, usableServices);

  // Redesign: PRICE is sortable. Client-side on purpose — `rank` is catalogue
  // order (100 / 200) and is already overloaded elsewhere, so it must not be
  // read as price, and the quotes arrive per service from the pricing engine
  // rather than from the catalogue.
  //
  // Starts UNSORTED (catalogue order), matching the mock: it lists Select
  // (4,600) above Save (4,025), which is not ascending — so the arrow there is
  // an affordance, not an active sort.
  const [priceSort, setPriceSort] = useState<'asc' | 'desc' | null>(null);

  const orderedServices = useMemo(() => {
    if (!priceSort) return services;
    // A service whose quote failed or is still loading sorts LAST in both
    // directions rather than as 0 — a missing price is not a cheap one, and
    // `undefined` in a numeric compare silently sinks to one end.
    const fee = (code: string) => {
      const value = quotes[code]?.fee;
      return typeof value === 'number' ? value : null;
    };
    return [...services].sort((a, b) => {
      const left = fee(a.code);
      const right = fee(b.code);
      if (left === null && right === null) return 0;
      if (left === null) return 1;
      if (right === null) return -1;
      return priceSort === 'asc' ? left - right : right - left;
    });
  }, [services, quotes, priceSort]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolledUp, setIsScrolledUp] = useState(false);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    const onScroll = () => setIsScrolledUp(element.scrollTop > 8);
    element.addEventListener('scroll', onScroll);
    return () => element.removeEventListener('scroll', onScroll);
  }, []);

  // A refresh or a deep link lands here with an empty store and nothing to
  // price. Send them back to the form rather than rendering a dead step.
  useEffect(() => {
    if (!orderPayload) {
      navigate(isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER, {
        replace: true,
      });
    }
  }, [orderPayload, isEditMode, editOrderUid, navigate]);

  // Fewer than two live services means there is nothing to choose between, and
  // a one-row step is a dead click. That is the catalogue's state on any box
  // ops has not seeded, so a deep link here has to fall through to the summary
  // rather than strand the order. Waits for the fetch: bouncing while the
  // catalogue is still loading would skip the step for everyone.
  useEffect(() => {
    // No plan (uses_services=false) = no service step at all.
    if (orderPayload && (!usesServices || (!isLoading && services.length < 2))) {
      navigate(ROUTE.ORDER_SUMMARY, { replace: true });
    }
  }, [orderPayload, isLoading, services.length, navigate, usesServices]);

  const backTo = isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER;

  const goToSummary = () => {
    if (!orderPayload) return;

    // `undefined`, never null or '': the payload is sent as-is, and an empty
    // string would read to the backend as "the merchant chose nothing named",
    // failing the live-service check instead of falling through to their rules.
    setOrderPayload({ ...orderPayload, service_code: chosen ?? undefined });
    // QA D5 — carry the FACT that this step was walked, so the summary's rail
    // does not erase it. Deliberately NOT inferred from `service_code`: the
    // pre-selected row is "use my saved rules", which sends no service_code at
    // all, so the most likely outcome of a [Change] visit would be the one case
    // still erased.
    navigate(ROUTE.ORDER_SUMMARY, { state: { fromServiceStep: true } });
  };

  // QA UI-F9 — Western digits, ALWAYS. This is aligning the step to the app,
  // not inventing a convention: claimHelpers, numberHelpers, ProductInputsRow
  // and the catalog modal all format with en-US, so the ar-IQ branch that used
  // to live here was the outlier. A price in Eastern Arabic numerals beside
  // metrics in Western ones is a worse inconsistency than either choice alone.
  const formatFee = (fee: number) =>
    `${new Intl.NumberFormat('en-US').format(fee)} ${String(t('iqd'))}`;

  return (
    <div className='sd-scope sd-font relative flex flex-col -mx-4 sm:-mx-8 -mt-6 -mb-6 h-[calc(100dvh-65px)] overflow-hidden'>
      {/* QA S3 — pb-36, not pb-28. The floating bar is ~90px tall including
          its bottom-4 offset, and 112px of padding left the last row of cards
          barely clearing it; a wrapped 4th card read as sitting underneath. */}
      <div ref={scrollRef} className='flex-1 overflow-y-auto px-4 sm:px-8 pt-4 sm:pt-6 pb-36'>
        <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground mb-6'>
          <Link
            to={ROUTE.ORDERS}
            className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
          >
            {String(t('orders'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <Link
            to={backTo}
            // Back to the edit form keeps this edit (create restores from the store).
            state={{ fromWizard: true }}
            className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
          >
            {String(t(isEditMode ? 'edit_order' : 'new_order'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <span className='text-sd-foreground font-medium'>{String(t('select_service_step'))}</span>
        </nav>

        {/* The merchant is demonstrably ON this step, which outranks the
            prediction that they would have skipped it. */}
        <OrderStepper currentStep={2} provinceCode={traceProvinceCode} serviceStepPresent />

        {/* Owner ruling 2026-08-25 (BOXY-214/215) — "mention it: your Save
            predefined doesn't exist for this province, so you should choose."

            This is the ONLY reason a merchant with a saved preference is
            standing here at all, so it is the first thing on the step, above
            the table. Without it they set Save in Settings, land on a page they
            were told they would skip, and are told nothing about why. */}
        <UnavailablePreferenceNotice
          unavailable={resolution?.unavailable_preference ?? null}
          refusal={serviceRefusal}
        />

        {isLoading ? (
          <div className='flex flex-col items-center justify-center py-20'>
            <Loader2 className='h-6 w-6 animate-spin text-sd-muted-foreground' strokeWidth={1.5} />
          </div>
        ) : (
          <>
            {/* QA/owner design round — the step is ONE enclosed table, not a
              scatter of floating cards.

              The card grid this replaces was structurally sound (it fixed
              UI-F2's starved fixed tracks) but it did not look like this app:
              every other wizard step — SelectCarrier above all — is a single
              `rounded-2xl border bg-sd-card shadow-sm` section with a header
              rule and divided rows, and four free-floating boxes read as a
              different product.

              ⚠️ This does NOT reintroduce UI-F2. That defect was fixed COLUMN
              TRACKS (`24px 1fr 1fr 140px 120px`) starving each other at 768 and
              auto-placing into debris below 640. There are no shared tracks
              here: the row is flex, the metrics wrap, and the price drops under
              the name on a phone instead of squeezing a track to nothing. */}
            <section className='rounded-2xl border border-sd-border bg-sd-card shadow-sm overflow-hidden'>
              <div className='px-5 py-4 border-b border-sd-border'>
                {/* The heading alone — the design's section head is the word
                  SERVICES and nothing else. `select_service_subtitle` stays in
                  both locale files: it said what the table below already shows,
                  and a key costs less than re-translating one. */}
                <h2 className='text-sm font-bold uppercase tracking-wider text-sd-foreground'>
                  {String(t('services'))}
                </h2>
              </div>

              {/* THE COLUMN SET IS SHARED BY EVERY ROW.
                Computed once, here, from the whole catalogue — not per
                service. That is the entire point: a metric occupies the same
                track on every row, so the two services can actually be read
                against each other.

                Measured failure this replaces (UI-QA, 1440 and 1024): with a
                wrapping chip row, Select carried "Successful delivery" where
                Save carried nothing, so every SHARED metric sat at a different
                x in each row. At 1024 the same metric was not even on the same
                visual line. Comparing the two services is the whole job of
                this screen.

                Only metrics SOMEBODY measures get a track — a column no
                service fills is dead space, not alignment. */}
              {/* THE COLUMN HEADINGS. Redesign moves the metric labels out of
                every row and into one header, which is what makes this read as
                a table rather than as a list of cards — and it removes N
                repetitions of the same Arabic label, which is where the
                truncation pressure came from.

                `lg`-only, matching TABLE_COLUMNS: below that the labels go back
                inline on each cell, because a heading with no column beneath it
                points at nothing. */}
              {metricColumns.length > 0 ? (
                <div
                  className={cn(
                    'hidden xl:grid items-center gap-x-4 px-5 py-2.5 border-b border-sd-border bg-sd-muted/20',
                    TABLE_COLUMNS[metricColumns.length] ?? TABLE_COLUMNS[3],
                  )}
                >
                  <span className='text-[11px] font-semibold uppercase tracking-wider text-sd-foreground/60'>
                    {String(t('name'))}
                  </span>
                  {metricColumns.map((id) => (
                    <span
                      key={id}
                      className='text-[11px] font-semibold uppercase tracking-wider text-sd-foreground/60'
                    >
                      {metricLabel(t, id)}
                    </span>
                  ))}
                  {/* The only interactive heading. `justify-self-end` so it
                    sits over the price it sorts, and the arrow rotates rather
                    than swapping glyph — a second IconData would be a second
                    asset for no gain. */}
                  <button
                    type='button'
                    onClick={() =>
                      setPriceSort((current) =>
                        current === 'asc' ? 'desc' : current === 'desc' ? null : 'asc',
                      )
                    }
                    aria-label={String(t('price'))}
                    className='inline-flex items-center gap-1 justify-self-end text-[11px] font-semibold uppercase tracking-wider text-sd-foreground/60 hover:text-sd-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring rounded'
                  >
                    {String(t('price'))}
                    <ArrowUp
                      className={cn(
                        'h-3 w-3 transition-transform',
                        priceSort === 'desc' && 'rotate-180',
                        !priceSort && 'opacity-40',
                      )}
                      strokeWidth={2}
                    />
                  </button>
                  <span aria-hidden='true' />
                </div>
              ) : null}

              <RadioGroup
                value={chosen ?? NO_SELECTION}
                onValueChange={selectService}
                className='gap-0'
                // Radix defaults to 'ltr'. Left unset the dot stays pinned left
                // in Arabic while every sibling mirrors — and Arabic is the
                // default locale here.
                dir={direction}
              >
                {/* 🔴 NO "USE MY SAVED RULES" ROW — owner's decision,
                  2026-08-25, and it follows from the step gate rather than
                  being a taste call.

                  THE RULE NOW: a merchant who set a standing preference SKIPS
                  this step entirely; only a merchant who chose "I'll pick for
                  each order" is sent here. So reaching this screen MEANS
                  nothing has been decided for them — and a row offering "apply
                  my rules" offered nothing to the only merchant who can see
                  it, while reading as a third product beside two real
                  services.

                  The row existed because the step could previously appear for
                  a merchant who HAD rules, and one stray tap would otherwise
                  strand them with no way back to "no explicit choice". That
                  cannot happen any more.

                  Still nothing pre-selected: `effective_default_service_code`
                  is the account default resolved with NO destination, so
                  committing it silently would override a province rule the
                  merchant never sees. They pick, or they go Back. */}
                {orderedServices.map((service) => {
                  // TEMP-SVC (Alhassan 2026-08-25) — a cold order cannot ship
                  // on a service that does not refrigerate, so the row is
                  // disabled with a reason rather than left pickable and
                  // refused by the API three screens later.
                  //
                  // ⚠️ ONLY an explicit `false` disables. Null means the
                  // backend has the code but not the migration, and every
                  // reader fails OPEN on unknown — the same rule the step
                  // decision uses. Taking a service away on a deploy gap is
                  // worse than briefly allowing one we will refuse.
                  const blockedForCold =
                    Boolean(orderPayload?.temperature_control) &&
                    service.temperature_control_supported === false;
                  // BOXY-113 §6.3 / BOXY-530 — same treatment: disabled, reason on the row.
                  const laneReason = blockedForCold ? undefined : blockedReasons.get(service.code);
                  const blocked = blockedForCold || Boolean(laneReason);
                  const active = chosen === service.code;
                  const metrics = buildServiceMetrics(t, service);
                  const metricsById = new Map(metrics.map((metric) => [metric.id, metric]));
                  const quote = quotes[service.code];
                  // 🔴 BOXY-239 — NEVER RECOMMEND A SERVICE THIS ORDER CANNOT
                  // USE. `effective_default_service_code` is resolved with no
                  // destination AND no temperature flag, so on a cold order it
                  // happily names the one service that cannot carry it — and
                  // the row directly above disables that same service. The
                  // merchant was shown "Boxy Save, recommended" greyed out and
                  // unpickable, which reads as the system recommending
                  // something and then refusing it.
                  //
                  // Reported by Alhassan as "the API returns this merchant
                  // without a preferred service, but the UI returns Save as the
                  // selected service, so I can't create temp-c orders".
                  const recommended =
                    !blocked && service.code === data?.effective_default_service_code;

                  return (
                    <label
                      key={service.code}
                      data-disabled={blocked ? 'true' : undefined}
                      className={cn(
                        'block px-5 py-4 border-b border-sd-border last:border-b-0 transition-colors',
                        blocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                        // The design's selected wash, #FCFBEA. `sd-primary/10`
                        // over the card lands on #FDFBED — the same colour to
                        // the eye, and it tracks the brand token instead of
                        // freezing a hex the theme cannot follow.
                        active && !blocked ? 'bg-sd-primary/10' : '',
                        blocked ? '' : 'hover:bg-sd-muted/30',
                      )}
                    >
                      <span
                        className={cn(
                          'flex flex-wrap items-start gap-3 xl:grid xl:items-center xl:gap-x-4',
                          TABLE_COLUMNS[metricColumns.length] ?? TABLE_COLUMNS[3],
                        )}
                      >
                        <RadioGroupItem
                          value={service.code}
                          aria-label={service.title}
                          disabled={blocked}
                          className='sr-only'
                        />

                        {/* THE NAME CELL — first grid track. Everything that
                          identifies the service lives here; the metrics are
                          SIBLINGS of this cell, not children of it, which is
                          what puts them in shared columns.

                          `max-w-3xl` caps the MEASURE, not the row: a
                          description is prose and prose set across 1300px runs
                          far past the ~90 characters an eye can track back
                          from. */}
                        <span className='flex min-w-0 flex-1 items-start gap-3 xl:flex-none'>
                          {/* The brand mark in a tile, per the redesign. The
                            tile tints with selection so the row reads as chosen
                            from its leading edge as well as its trailing one —
                            colour on one side only is easy to miss at a glance
                            on a wide row.

                            ServiceIcon renders NOTHING for a service with no
                            glyph, so the tile keeps the text aligned without
                            standing in a wrong mark. */}
                          <span
                            className={cn(
                              'mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors',
                              active
                                ? 'border-sd-primary bg-sd-primary/20'
                                : 'border-sd-border bg-sd-muted/40',
                            )}
                          >
                            <ServiceIcon
                              code={service.code}
                              className='h-5 w-5 text-sd-foreground'
                            />
                          </span>

                          <span className='min-w-0 flex-1 max-w-3xl'>
                            <span className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                              {/* QA S4 — <bdi> for the same reason the price and
                              the metric values carry it. A service name is a
                              Latin brand inside an Arabic page, and isolating
                              only the NUMBERS left the prose beside them
                              unprotected. */}
                              <bdi className='text-sm font-semibold text-sd-foreground'>
                                {service.title}
                              </bdi>
                              {recommended ? (
                                <span className='inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700'>
                                  <Sparkles className='h-3 w-3' strokeWidth={2} />
                                  {String(t('recommended'))}
                                </span>
                              ) : null}
                            </span>

                            {service.description ? (
                              <bdi className='mt-1 block text-xs text-sd-foreground/75 line-clamp-2'>
                                {service.description}
                              </bdi>
                            ) : null}

                            {/* The reason, ON the row it explains. A disabled row
                            with the explanation somewhere else is the same
                            silence this whole ticket was about — the merchant
                            sees a greyed choice and no way to know why. */}
                            {blockedForCold ? (
                              <bdi
                                data-testid='service-cold-unsupported'
                                className='mt-1 block text-xs font-medium text-amber-700 dark:text-amber-500'
                              >
                                {String(
                                  t('service_no_temperature_control', {
                                    defaultValue:
                                      'Not available with temperature control. Turn it off, or choose a service that supports it.',
                                  }),
                                )}
                              </bdi>
                            ) : null}
                            {laneReason ? (
                              <bdi
                                data-testid={
                                  {
                                    remote_area: 'service-remote-unsupported',
                                    pickup_not_served: 'service-pickup-unsupported',
                                    multi_box: 'service-multibox-unsupported',
                                  }[laneReason] ?? 'service-destination-unsupported'
                                }
                                className='mt-1 block text-xs font-medium text-amber-700 dark:text-amber-500'
                              >
                                {laneReason === 'remote_area'
                                  ? String(
                                      t('service_no_remote_area', {
                                        defaultValue: 'Not available for remote areas',
                                      }),
                                    )
                                  : laneReason === 'pickup_not_served'
                                    ? String(
                                        t('service_no_pickup_province', {
                                          province: resolution?.pickup_province ?? '',
                                        }),
                                      )
                                    : laneReason === 'multi_box'
                                      ? String(t('service_no_multi_box'))
                                      : String(t('service_no_destination'))}
                              </bdi>
                            ) : null}
                            {active && service.code === remoteFallback ? (
                              <bdi
                                data-testid='service-remote-fallback-note'
                                className='mt-1 block text-xs text-sd-foreground/75'
                              >
                                {String(t('service_remote_fallback_note'))}
                              </bdi>
                            ) : null}
                          </span>
                        </span>

                        {/* THE METRIC CELLS — one grid child each, so a metric
                          occupies the SAME track on every row and the two
                          services can actually be read against each other.

                          A service nobody has measured renders NOTHING here —
                          not a dash, not a zero, both of which read as a
                          measured result. A service that measures SOME of the
                          set gets a blank cell in the tracks it does not fill,
                          which claims nothing while keeping the columns
                          comparable.

                          ⚠️ Below `lg` there are no tracks, so each cell
                          carries its own label inline; from `lg` up the label
                          is in the header and repeating it would be noise. */}
                        {/* ⚠️ `xl:contents` IS THE LOAD-BEARING CLASS.
                          Below xl this is a real flex container taking a FULL
                          LINE, so the metrics wrap among themselves and never
                          compete with the name for width. At xl `display:
                          contents` removes the wrapper from layout entirely and
                          the cells become direct grid children of the row —
                          which is what keeps the header's tracks and the row's
                          tracks the same tracks.

                          🔴 THIS REPLACES A FLOOR THAT MADE THINGS WORSE. I
                          first fixed the 768 collision with min-w-[7rem] on the
                          cells, competing in the SAME line as the name. UI-QA
                          measured the result: the five metrics held their 112px
                          and the NAME collapsed to 48px, so Boxy Select went
                          from 0 overlaps to 2 — the fix moved the starvation
                          rather than removing it.

                            ⭐ A MINIMUM ON SOME TRACKS IS A MAXIMUM ON THE
                              OTHERS. Same arithmetic as the 1024 grid, one
                              layout regime over: 5×112 = 560 plus name, price
                              and action does not fit 640, and never could.

                          Giving the metrics their own line removes the
                          competition instead of re-allocating it. */}
                        <span className='flex w-full flex-wrap items-start gap-x-4 gap-y-2 xl:contents'>
                          {metricColumns.map((id) => {
                            const metric = metricsById.get(id);

                            // Held rather than skipped: dropping the cell would
                            // collapse the track and shift every metric after it,
                            // which is the defect this layout exists to remove.
                            if (!metric) {
                              return (
                                <span
                                  key={id}
                                  data-metric={id}
                                  aria-hidden='true'
                                  className='hidden xl:block'
                                />
                              );
                            }

                            const Icon = metricIcon(id);
                            return (
                              <span
                                key={id}
                                data-metric={id}
                                className='min-w-[6rem] text-xs xl:min-w-0'
                              >
                                <span className='flex items-start gap-1.5 text-sd-foreground/75 xl:hidden'>
                                  <Icon className='mt-0.5 h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
                                  {/* WRAPS, never truncates. A label cut at 89px
                                  is unreadable and two of these become
                                  indistinguishable from each other. */}
                                  <span className='min-w-0'>{metric.label}</span>
                                </span>
                                {/* <bdi>: a measurement is a mixed run, and an
                                un-isolated one rendered "16-20" as "20-16"
                                (QA UI-F4). */}
                                <bdi className='mt-0.5 block font-semibold text-sd-foreground xl:mt-0'>
                                  {metric.value}
                                </bdi>
                                {/* The redesign's sub-label, and it earns its
                                place only under the delivery window — the one
                                metric whose value is a duration rather than a
                                rate, so "16 – 20 hrs" alone does not say what
                                is being timed. */}
                                {id === 'delivery' ? (
                                  <span className='mt-0.5 hidden text-[11px] text-sd-foreground/60 xl:block'>
                                    {String(t('est_delivery'))}
                                  </span>
                                ) : null}
                              </span>
                            );
                          })}
                        </span>

                        {/* Price — its own track from `xl`, pulled to the row's
                          end below that. */}
                        <span className='ms-auto shrink-0 text-end text-base font-bold text-sd-foreground xl:ms-0 xl:justify-self-end'>
                          {quote?.isLoading ? (
                            <Loader2
                              className='h-4 w-4 animate-spin text-sd-muted-foreground'
                              strokeWidth={1.5}
                            />
                          ) : typeof quote?.fee === 'number' ? (
                            <bdi>{formatFee(quote.fee)}</bdi>
                          ) : (
                            // A failed quote leaves the row selectable on
                            // purpose: eligibility is the backend's answer and
                            // it refuses with a reason at creation, rather than
                            // being second-guessed here.
                            <span className='text-sd-muted-foreground'>—</span>
                          )}
                        </span>

                        <span className='shrink-0 xl:justify-self-end'>
                          <SelectPill active={active} t={t} />
                        </span>
                      </span>
                    </label>
                  );
                })}
              </RadioGroup>
            </section>
          </>
        )}
      </div>

      <div className='absolute bottom-4 start-0 end-0 z-30 px-4 sm:px-8'>
        <div
          className={cn(
            'rounded-2xl border border-sd-border bg-sd-card px-5 py-4 flex items-center justify-between transition-shadow',
            isScrolledUp && 'shadow-lg',
          )}
        >
          <Link
            to={backTo}
            // Back to the edit form keeps this edit (create restores from the store).
            state={{ fromWizard: true }}
            className='inline-flex justify-center whitespace-nowrap items-center gap-2 h-10 rounded-md border border-sd-border bg-transparent px-4 text-sm font-medium uppercase text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2'
          >
            <ArrowLeft className={cn('h-4 w-4', isRTL && 'rotate-180')} strokeWidth={2} />
            {String(t('back'))}
          </Link>
          {/* 🔴 GATED ON AN ACTUAL PICK — this used to be ALWAYS ENABLED, and
              the comment defending it was true under a design that no longer
              exists: "'I did not choose' is a valid answer that means 'apply
              my saved rules'". It was, while the saved-rules row existed and
              this step was advisory. Under the owner's rule of 2026-08-25 the
              step is shown ONLY to a merchant who chose "for each order", so
              continuing without a pick lets the resolution ladder decide for
              exactly the merchant who asked to decide for themselves.

              ⭐ THE ESCAPE HATCH SURVIVES, NARROWED. A merchant must not be
              trapped on a screen with nothing to pick, so an errored/empty
              catalogue still lets them through — "nothing to choose from" and
              "something to choose from and they have not chosen" are different
              states and only the second one blocks. BACK is always available
              either way. */}
          {/* 🔴 A DISABLED CONTROL MUST SAY WHAT IT IS WAITING FOR.
              BOXY::QA, rendered walk 2026-09-21: they pressed CONTINUE five
              times and nothing happened — no navigation, no network call, no
              message. On a system where every API call returns 200, that is
              indistinguishable from "I cannot create the order", and it is the
              screen the owner reported being stuck on.

              The row IS clickable (the whole thing is a <label> around the
              radio, so the Select pill selects too) — what was missing was any
              statement of WHY the button is dead. Rendered + disabled + a
              reason, never a control that fails silently. */}
          {services.length > 0 && chosen === null ? (
            <p id='continue-needs-service' className='self-center text-xs text-sd-muted-foreground'>
              {String(t('choose_a_service_to_continue'))}
            </p>
          ) : null}
          <button
            type='button'
            onClick={goToSummary}
            aria-describedby={
              services.length > 0 && chosen === null ? 'continue-needs-service' : undefined
            }
            title={
              services.length > 0 && chosen === null
                ? String(t('choose_a_service_to_continue'))
                : undefined
            }
            disabled={services.length > 0 && chosen === null}
            className='inline-flex justify-center whitespace-nowrap items-center gap-2 h-10 rounded-md px-6 text-sm font-medium uppercase bg-sd-primary text-sd-primary-foreground hover:opacity-90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45'
          >
            {String(t('continue'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectService;
